import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConsultationForm = ({ isVisible, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    serviceType: '',
    projectBudget: '',
    timeline: '',
    projectDescription: '',
    currentWebsite: '',
    marketingGoals: [],
    preferredMeeting: '',
    hearAboutUs: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});

  const serviceOptions = [
    { value: 'web-development', label: 'Web Development' },
    { value: 'app-development', label: 'App Development' },
    { value: 'social-media', label: 'Social Media Marketing' },
    { value: 'full-package', label: 'Complete Digital Transformation' },
    { value: 'consultation', label: 'Strategy Consultation Only' }
  ];

  const budgetOptions = [
    { value: '5K-15K', label: '₹5,000 - ₹15,000' },
    { value: '15K-30K', label: '₹15,000 - ₹30,000' },
    { value: '30K-50k', label: '₹30,000 - ₹50,000' },
    { value: '50k-100k', label: '₹50,000 - ₹100,000' },
    { value: '100k+', label: '₹100,000+' },
    { value: 'discuss', label: 'Let\'s Discuss' }
  ];

  const timelineOptions = [
    { value: 'asap', label: 'ASAP (Rush Project)' },
    { value: '1-2months', label: '1-2 Months' },
    { value: '3-4months', label: '3-4 Months' },
    { value: '6months+', label: '6+ Months' },
    { value: 'flexible', label: 'Flexible Timeline' }
  ];

  const meetingOptions = [
    { value: 'video', label: 'Video Call (Zoom/Teams)' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'in-person', label: 'In-Person Meeting' },
    { value: 'email', label: 'Email Discussion' }
  ];

  const marketingGoalOptions = [
    'Increase Brand Awareness',
    'Generate More Leads',
    'Improve Customer Engagement',
    'Boost Online Sales',
    'Build Social Media Presence',
    'Enhance SEO Rankings'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleMarketingGoalChange = (goal, checked) => {
    setFormData(prev => ({
      ...prev,
      marketingGoals: checked 
        ? [...prev?.marketingGoals, goal]
        : prev?.marketingGoals?.filter(g => g !== goal)
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData?.name?.trim()) newErrors.name = 'Name is required';
      if (!formData?.email?.trim()) newErrors.email = 'Email is required';
      if (!formData?.serviceType) newErrors.serviceType = 'Please select a service type';
    }
    
    if (step === 2) {
      if (!formData?.projectBudget) newErrors.projectBudget = 'Please select a budget range';
      if (!formData?.timeline) newErrors.timeline = 'Please select a timeline';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateStep(currentStep)) {
      console.log('Form submitted:', formData);
      // Handle form submission
      onClose();
    }
  };

  if (!isVisible) return null;

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Full Name"
          type="text"
          placeholder="John Smith"
          value={formData?.name}
          onChange={(e) => handleInputChange('name', e?.target?.value)}
          error={errors?.name}
          required
        />
        
        <Input
          label="Email Address"
          type="email"
          placeholder="john@company.com"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Company Name"
          type="text"
          placeholder="Your Company"
          value={formData?.company}
          onChange={(e) => handleInputChange('company', e?.target?.value)}
        />
        
        <Input
          label="Phone Number"
          type="tel"
          placeholder="+91-7982843491"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
        />
      </div>

      <Select
        label="Primary Service Interest"
        placeholder="Select the service you're most interested in"
        options={serviceOptions}
        value={formData?.serviceType}
        onChange={(value) => handleInputChange('serviceType', value)}
        error={errors?.serviceType}
        required
      />

      <Input
        label="Current Website (Optional)"
        type="url"
        placeholder="https://yourwebsite.com"
        value={formData?.currentWebsite}
        onChange={(e) => handleInputChange('currentWebsite', e?.target?.value)}
        description="If you have an existing website, we'd love to take a look"
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Project Budget Range"
          placeholder="Select your budget range"
          options={budgetOptions}
          value={formData?.projectBudget}
          onChange={(value) => handleInputChange('projectBudget', value)}
          error={errors?.projectBudget}
          required
        />
        
        <Select
          label="Desired Timeline"
          placeholder="When do you need this completed?"
          options={timelineOptions}
          value={formData?.timeline}
          onChange={(value) => handleInputChange('timeline', value)}
          error={errors?.timeline}
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Marketing Goals (Select all that apply)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {marketingGoalOptions?.map((goal) => (
            <Checkbox
              key={goal}
              label={goal}
              checked={formData?.marketingGoals?.includes(goal)}
              onChange={(e) => handleMarketingGoalChange(goal, e?.target?.checked)}
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Project Description
        </label>
        <textarea
          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
          rows={4}
          placeholder="Tell us about your project, goals, and any specific requirements..."
          value={formData?.projectDescription}
          onChange={(e) => handleInputChange('projectDescription', e?.target?.value)}
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <Select
        label="Preferred Meeting Type"
        placeholder="How would you like to meet?"
        options={meetingOptions}
        value={formData?.preferredMeeting}
        onChange={(value) => handleInputChange('preferredMeeting', value)}
        required
      />

      <Input
        label="How did you hear about us?"
        type="text"
        placeholder="Google search, referral, social media, etc."
        value={formData?.hearAboutUs}
        onChange={(e) => handleInputChange('hearAboutUs', e?.target?.value)}
      />

      <div className="bg-surface p-6 rounded-lg">
        <h4 className="font-semibold text-foreground mb-3">What happens next?</h4>
        <div className="space-y-2 text-sm text-muted-foreground">
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={16} className="text-success" />
            <span>We'll review your information within 24 hours</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={16} className="text-success" />
            <span>Schedule a personalized strategy session</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Check" size={16} className="text-success" />
            <span>Receive a custom proposal tailored to your needs</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-primary">Book Your Free Consultation</h3>
              <p className="text-muted-foreground">Step {currentStep} of 3</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={24} />
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex space-x-2">
              {[1, 2, 3]?.map((step) => (
                <div
                  key={step}
                  className={`flex-1 h-2 rounded-full ${
                    step <= currentStep ? 'bg-accent' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          <div className="flex justify-between mt-8">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Previous
              </Button>
            )}
            
            <div className="ml-auto">
              {currentStep < 3 ? (
                <Button
                  type="button"
                  onClick={handleNext}
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="bg-accent hover:bg-accent/90"
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  type="submit"
                  className="bg-conversion hover:bg-conversion/90"
                  iconName="Send"
                  iconPosition="right"
                >
                  Book Consultation
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsultationForm;